<template>
    <div>
        <b-modal :active.sync="showModal" :id="`createcreditinvoice_${number}`" aria-modal :width="800" scroll="keep">
            <header class="modal-card-head">
                <p class="modal-card-title">Creditfactuur aanmaken (optioneel)</p>
                <b-tooltip :label="hideSensitiveData ? 'Toon gevoelige data' : 'Verberg gevoelige data'" position="is-left">
                    <b-button :type="hideSensitiveData ? 'is-success' : 'is-danger'" :icon-right="hideSensitiveData ? 'eye' : 'eye-slash'" @click="$store.dispatch('toggleSensitiveData')"></b-button>
                </b-tooltip>
            </header>
            <section class="modal-card-body text-left" style="border-radius:0;">
                <p v-if="message && message.length">{{ message }}</p>

                <!-- Clear explanation for customers about what they're seeing -->
                <div class="notification is-dark is-light mb-4">
                    <div class="content">
                        <div v-if="isAddendumRemovalScenario">
                            <h6 class="title"><strong>Borg retour bij contractaanpassing</strong></h6>
                            <p>
                                Eén of meer boxen of producten zijn verwijderd uit het contract. Selecteer hieronder de borgregels die je wilt crediteren zodat de borg kan worden terugbetaald.
                            </p>
                            <ul>
                                <li><strong>Borgregels:</strong> standaard geselecteerd voor creditering</li>
                                <li>Productregels kun je optioneel ook selecteren als je die wilt terugboeken</li>
                            </ul>
                        </div>

                        <div v-else-if="Array.isArray(extraProducts) && extraProducts.length > 0">
                            <h6 class="title"><strong>Contract beëindiging scenario</strong></h6>
                            <p v-if="hasSelectedProducts">
                                Het contract is beëindigd waarvoor de laatste factuur al is aangemaakt. Wij raden aan de geselecteerde borg- en productregels te crediteren:
                            </p>
                            <p v-else>
                                Het contract is beëindigd waarvoor nog geen laatste factuur bestaat, of waar verrekening niet van toepassing is. Wij raden aan alleen de borgregels te crediteren{{ hasProrataProducts ? '; de overige product(en) worden later automatisch op een pro-rata factuur verwerkt' : '' }}:
                            </p>
                            <ul>
                                <li v-if="hasProrataProducts"><strong>Pro-rata factuurregels:</strong> van het begin van de factuurperiode tot de geselecteerde einddatum (x dagen)</li>
                                <li><strong>Borgregels{{ hasSelectedProducts ? ' en producten' : '' }}:</strong> automatisch geselecteerd voor credittering</li>
                                <li v-if="hasSelectedProducts">Borg en product(en) worden verrekend op de bestaande factuur (indien mogelijk)</li>
                                <li v-else-if="hasProrataProducts">Producten worden later op een pro-rata factuur verwerkt</li>
                            </ul>
                        </div>

                        <div v-else-if="deposit_products && deposit_products.length > 0">
                            <h6 class="title"><strong>Borg verrekening scenario</strong></h6>
                            <p v-if="isContractScenario && hasSelectedProducts">
                                Het contract is beëindigd waarvoor de laatste factuur al is aangemaakt. Wij raden aan alleen de borgregels te selecteren:
                            </p>
                            <p v-else-if="isContractScenario && !hasSelectedProducts">
                                Het contract is beëindigd waarvoor nog geen laatste factuur bestaat, of waar verrekening niet van toepassing. Wij raden aan alleen de borgregels te crediteren{{ hasProrataProducts ? '; producten worden later automatisch op een pro-rata factuur verwerkt' : '' }}:
                            </p>
                            <p v-else>
                                Je hebt een bestaande factuur waarop je de borg kunt crediteren. Wij raden aan alleen de borgregels te selecteren:
                            </p>
                            <ul>
                                <li><strong>Borgregels:</strong> automatisch geselecteerd voor credittering</li>
                                <li v-if="hasSelectedProducts">Borg wordt verrekend op de bestaande factuur (indien mogelijk en als de factuur nog niet verstuurd is)</li>
                                <li v-else-if="hasProrataProducts">Producten worden later op een pro-rata factuur verwerkt</li>
                            </ul>
                        </div>

                        <div v-else>
                            <h6 class="title"><strong>Directe factuur credit scenario</strong></h6>
                            <p v-if="isContractScenario">
                                Het contract is beëindigd. Je kunt direct zowel producten als borg crediteren. Selecteer hieronder wat je wilt terugboeken:
                            </p>
                            <p v-else>
                                Je kunt direct zowel producten als borg crediteren. Selecteer hieronder wat je wilt terugboeken:
                            </p>
                            <ul>
                                <li><strong>Alle producten en borgregels</strong> van de factuur zijn zichtbaar</li>
                                <li>Kies welke producten en/of borg je wilt crediteren</li>
                                <li>De credit wordt direct verwerkt op de bestaande factuur (indien mogelijk en als de factuur nog niet verstuurd is)</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <b-table
                    :data="displayProducts"
                    :checked-rows.sync="selectedProducts"
                    :custom-is-checked="isRowSelected"
                    checkable
                    checkbox-position="left"
                >
                    <template>
                        <b-table-column v-slot="props" field="name" label="Product" width="200">
                            {{ props.row.name }}
                        </b-table-column>

                        <b-table-column v-slot="props" field="name" label="Aantal" width="100">
                            {{ props.row.amount || 1 }}
                        </b-table-column>

                        <b-table-column v-slot="props" field="deposit" label="Borg" width="100" numeric>
                            <span v-if="hideSensitiveData">&euro;&nbsp;********</span>
                            <span v-else-if="props.row.deposit > 0">&euro;{{ number_format(props.row.deposit * (props.row.amount || 1)) }}</span>
                            <span v-else>-</span>
                        </b-table-column>

                        <b-table-column v-slot="props" field="price" label="Totaal" width="100" numeric>
                            <span v-if="hideSensitiveData">&euro;&nbsp;********</span>
                            <span v-else-if="props.row.price > 0">&euro;&nbsp;{{ number_format(props.row.price * (props.row.amount || 1)) }}</span>
                            <span v-else>-</span>
                        </b-table-column>
                    </template>
                </b-table>
            </section>
            <footer class="modal-card-foot">
                <div class="level is-mobile" style="width: 100%;">
                    <div class="level-left">
                        <div class="level-item">
                            <small class="has-text-grey">
                                <span v-if="selectedProducts.length > 0">
                                    {{ selectedProducts.length }} {{ selectedProducts.length === 1 ? 'regel' : 'regels' }} geselecteerd
                                </span>
                                <span v-else>Geen regels geselecteerd</span>
                            </small>
                        </div>
                    </div>
                    <div class="level-right">
                        <div class="level-item">
                            <b-button type="is-light" @click="showModal = false">Annuleren (geen creditfactuur)</b-button>
                            <b-button type="is-success" @click="createCreditInvoice" :disabled="creating || !selectedProducts.length" :loading="creating">Maak een creditfactuur</b-button>
                        </div>
                    </div>
                </div>
            </footer>
        </b-modal>

        <b-button @click="toggleModal" v-if="!hideButton && !user.deurwaarder" :outlined="hasCredits" type="is-primary">{{ hasCredits ? 'Nog een creditfactuur maken' : 'Creditfactuur aanmaken' }}</b-button>
    </div>
</template>

<script>
export default {
    name: 'CreateCreditInvoiceButton',

    props: {
        deposit_products: {
            type: Array,
            required: false,
            default: []
        },
        endDate: {
            type: String,
            required: false,
            default: null
        },
        products: {
            type: Array,
            required: false,
            default: []
        },
        extraProducts: {
            type: Array,
            required: false,
            default: () => []
        },
        customerId: {
            type: Number|String,
            required: true
        },
        // used for a unique modal id.
        number: {
            type: String,
            required: true
        },
        // Invoice ID for credit_of relationship
        invoiceId: {
            type: Number|String,
            required: false,
            default: null
        },
        contractId: {
            type: Number|String,
            required: false,
            default: null
        },
        hasCredits: {
            type: Boolean,
            default: false
        },
        hideTheButton: {
            type: Boolean,
            default: false
        },
        message: {
            type: String,
            required: false,
            default: ''
        },
        scenarioType: {
            type: String,
            required: false,
            default: 'default'
        }
    },

    data() {
        return {
            initialized: false,
            showModal: false,
            selectedProducts: [],
            baseSelection: [], // Store the initial selection for static messaging
            creating: false,
        }
    },

    computed: {
        user() {
            return this.$store.state.user
        },

        hideSensitiveData() {
            return this.$store.state.hideSensitiveData
        },

        hideButton() {
            let hideButton = false

            if(_.has(this.user.organisation_settings, 'strictcredits') && this.user.organisation_settings.strictcredits == 1 && (this.user.role != 'pit' && !this.user.is_admin)){
                hideButton = true
            }

            return this.hideTheButton ? true : hideButton
        },

                // Check if we have selected products (not just deposits) - indicates invoice exists
        hasSelectedProducts() {
            return this.baseSelection.some(product => product.totalType === 'price' && parseFloat(product.price || 0) > 0)
        },

        // Check if this is a contract scenario (has contract ID and end date)
        isContractScenario() {
            return this.contractId && this.endDate && this.contractId !== 'null' && this.contractId !== null
        },

        isAddendumRemovalScenario() {
            return this.scenarioType === 'addendum_remove'
        },

        // Check if we have pro-rata products selected (with days in name)
        hasProrataProducts() {
            // Check if we have actual pro-rata products selected
            const hasSelectedProrata = this.baseSelection.some(product =>
                product.name && product.name.includes('dagen') && product.totalType === 'price'
            )

            // Also check if this is a contract scenario where we don't have products selected
            // (indicating future end date where pro-rata invoice will be created)
            const isFutureContractEnd = this.isContractScenario && !this.hasSelectedProducts

            return hasSelectedProrata || isFutureContractEnd
        },

        // Show prorated lines from end-contract flow and deposit lines, plus all products from direct invoice credit
        displayProducts() {
            const transform = (rows, includeFullPrice = false) => {
                const out = []
                const seenKeys = new Set()
                const pushUnique = (row, key) => {
                    if (!seenKeys.has(key)) {
                        seenKeys.add(key)
                        out.push(row)
                    }
                }
                ;(rows || []).forEach(r => {
                    const baseId = (r.product || r.id)
                    const invoice = r.invoice || null
                    const name = r.name
                    const amount = r.amount || 1
                    const btw = r.btw

                    // Include full price lines if explicitly requested
                    if (includeFullPrice && parseFloat(r.price || 0) > 0) {
                        const row = {
                            ...r,
                            id: baseId,
                            product: baseId,
                            name: name,
                            amount,
                            btw,
                            price: parseFloat(r.price),
                            deposit: 0,
                            totalType: 'price',
                            invoice
                        }
                        pushUnique(row, `${baseId}-price-${invoice || 'none'}-${amount}-${parseFloat(r.price || 0)}`)
                    }

                    // Include deposit lines with "Borg van" prefix and 0% BTW
                    if (parseFloat(r.deposit || 0) > 0) {
                        // Remove "(X dagen)" or "(X maanden)" text from deposit product names
                        const cleanName = name.replace(/\s*\(\d+\s+(dag|dagen|maand|maanden)\)\s*$/i, '')
                        const row = {
                            ...r,
                            id: `${baseId}-deposit`,
                            product: baseId,
                            name: `Borg van ${cleanName}`,
                            amount,
                            btw: 0,
                            price: 0, // No price for deposits
                            deposit: parseFloat(r.deposit), // Deposit amount goes in deposit field
                            totalType: 'deposit',
                            invoice
                        }
                        pushUnique(row, `${baseId}-deposit-${invoice || 'none'}-${amount}-${parseFloat(r.deposit || 0)}`)
                    }
                })
                return out
            }

            // Two scenarios:
            // 1. End-contract flow: show prorated lines (extraProducts) + deposit lines from base products
            // 2. Direct invoice credit: show all products from the invoice

            if (Array.isArray(this.extraProducts) && this.extraProducts.length > 0) {
                // End-contract flow: prorated lines + deposits only
                const extras = transform(this.extraProducts, true) // Include prorated price lines
                const baseDepositsOnly = transform(this.products, false) // Include only deposit lines from ALL base products
                const result = [...extras, ...baseDepositsOnly]

                return result
            } else {
                // Direct invoice credit: show all products from the invoice
                return transform(this.products, true) // Include all price and deposit lines
            }
        }
    },

    watch: {
        // Watch for changes in products array that might affect selection
        products: {
            handler(newProducts, oldProducts) {
                if (this.showModal && newProducts && oldProducts && newProducts.length !== oldProducts.length) {
                    this.$nextTick(() => {
                        this.selectProducts()
                    })
                }
            },
            deep: true
        },

        displayProducts: {
            handler(newDisplayProducts) {
                if (!this.showModal) {
                    return
                }

                this.$nextTick(() => {
                    this.syncSelectedProductsWithDisplay()

                    if (this.selectedProducts.length === 0 && newDisplayProducts.length > 0) {
                        this.selectProducts()
                    }
                })
            }
        },
        showModal(value) {
            if (!value) {
                this.selectedProducts = []
                this.baseSelection = []
                this.initialized = false
                return
            }

            this.$nextTick(() => {
                if (!this.initialized) {
                    this.selectProducts()
                }
            })
        }
    },

    created() {
        if(_.has(this.$route, 'query.creditinvoice')){
            this.toggleModal()
        }
    },

    methods: {
        toggleModal() {
            this.showModal = !this.showModal
        },

        createCreditInvoice() {
            if (this.creating) return
            this.creating = true
            const byKey = (p) => (p.product || p.id)
            const selectedVisibleRows = this.getVisibleSelectedProducts()

            if (selectedVisibleRows.length === 0) {
                this.$buefy.toast.open({message: 'Selecteer minimaal één zichtbare regel.', type: 'is-danger'})
                this.creating = false
                return
            }

            const rawRows = selectedVisibleRows.map(sp => ({
                product: byKey(sp),
                name: sp.name,
                amount: sp.amount || 1,
                btw: (sp.totalType === 'deposit') ? 0 : sp.btw,
                price: parseFloat(sp.price || 0),
                deposit: parseFloat(sp.deposit || 0), // Include deposit amount
                totalType: sp.totalType || (parseFloat(sp.price || 0) > 0 ? 'price' : 'deposit'),
                invoice: sp.invoice || null
            }))
            const seenRows = new Set()
            const rows = rawRows.filter((row) => {
                const key = this.isAddendumRemovalScenario
                    ? `${row.product}-${row.totalType}`
                    : `${row.product}-${row.totalType}-${row.amount}-${row.price}-${row.deposit}-${row.invoice || 'none'}`
                if (seenRows.has(key)) {
                    return false
                }
                seenRows.add(key)
                return true
            })

            const candidate = selectedVisibleRows.find(x => x.invoice) || selectedVisibleRows[0]

            // Prevent creating credit invoices on other credit invoices
            // Only set creditOf if we can verify it's not a credit invoice type
            let creditOf = null

            // First check if we have an explicit invoiceId prop (from invoice page)
            if (this.invoiceId) {
                // Check if current route is for a credit invoice
                if (this.$route.params.type === 'credit') {
                    console.warn('Cannot create credit invoice on a credit invoice. Skipping credit_of assignment.')
                    creditOf = null
                } else {
                    creditOf = this.invoiceId
                }
            } else if (candidate && candidate.invoice) {
                // Fallback to candidate invoice (from contract end flow)
                if (this.$route.params.type === 'credit') {
                    console.warn('Cannot create credit invoice on a credit invoice. Skipping credit_of assignment.')
                    creditOf = null
                } else {
                    creditOf = candidate.invoice
                }
            }

            const payload = {
                organisation: this.normalizeId(this.user.organisation),
                customer_id: this.normalizeId(this.customerId),
                contract_id: this.normalizeId(this.contractId),
                credit_of: this.normalizeId(creditOf),
                end_date: this.endDate || null,
                lines: rows
            }

            axios.post('/api/credit-invoice/draft', payload, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then((response) => {
                const id = _.get(response, 'data.id')
                if (id) {
                    this.$router.push({name: 'CreditInvoiceRecord', query: { invoice: id, draft: true }})
                } else {
                    this.$buefy.toast.open({message: 'Credit concept aangemaakt', type: 'is-success'})
                    this.showModal = false
                }
            }).catch(() => {
                this.$buefy.toast.open({message: 'Kon credit concept niet aanmaken', type: 'is-danger'})
            }).then(() => {
                this.creating = false
            })
        },

        selectProducts() {
            // Clear any existing selections first
            this.selectedProducts = []

            const all = this.displayProducts

            // Two scenarios:
            // 1. End-contract flow: auto-select all visible products
            // 2. Direct invoice credit: select based on deposit_products logic

            if (this.isAddendumRemovalScenario) {
                this.$set(this, 'selectedProducts', all.filter(product => product.totalType === 'deposit'))
            } else if (Array.isArray(this.extraProducts) && this.extraProducts.length > 0) {
                // End-contract flow: select all visible products automatically
                // Use Vue.set to ensure reactivity and create new array references
                this.$set(this, 'selectedProducts', [...all])
            } else {
                // Direct invoice credit: use original logic but only for visible products
                _.each(all, (product) => {
                    if (this.deposit_products.length) {
                        const hasDeposit = _.find(this.deposit_products, (dp) => (dp.product || dp.id) == (product.product || product.id))
                        if (hasDeposit && product.totalType === 'deposit') {
                            this.selectedProducts.push(product)
                        }
                    } else {
                        this.selectedProducts.push(product)
                    }
                })
            }

            // Store the base selection for static messaging
            this.baseSelection = [...this.selectedProducts]
            this.syncSelectedProductsWithDisplay()
            this.initialized = true
        },
        getRowKey(row) {
            if (!row) {
                return 'empty'
            }
            return [
                row.totalType || (parseFloat(row.price || 0) > 0 ? 'price' : 'deposit'),
                row.product || row.id || '',
                row.invoice || 'none',
                row.name || '',
                Number(row.amount || 1),
            ].join('::')
        },
        isRowSelected(currentRow, checkedRow) {
            return this.getRowKey(currentRow) === this.getRowKey(checkedRow)
        },
        getVisibleSelectedProducts() {
            const displayMap = new Map((this.displayProducts || []).map(row => [this.getRowKey(row), row]))
            const selected = []
            const seen = new Set()

            ;(this.selectedProducts || []).forEach((row) => {
                const key = this.getRowKey(row)
                if (!displayMap.has(key) || seen.has(key)) {
                    return
                }
                seen.add(key)
                selected.push(displayMap.get(key))
            })

            return selected
        },
        syncSelectedProductsWithDisplay() {
            this.$set(this, 'selectedProducts', this.getVisibleSelectedProducts())
        },

        number_format(val, decimals = 2) {
            val = parseFloat(val)
            return val.toFixed(decimals)
        },
        normalizeId(value) {
            if (value === null || value === undefined || value === '' || value === 'null') {
                return null
            }

            if (Array.isArray(value)) {
                if (value.length === 0) {
                    return null
                }
                return this.normalizeId(value[0])
            }

            if (typeof value === 'object') {
                if (Object.prototype.hasOwnProperty.call(value, 'id')) {
                    return this.normalizeId(value.id)
                }
                return null
            }

            const parsed = Number(value)
            return Number.isFinite(parsed) ? parsed : null
        }
    }


}
</script>
